import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const fmtHora = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export function ChatDialog({ open, onOpenChange, entrega, currentUserId, title }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!entrega?.id) return;
    try {
      const r = await api.get(`/entregas/${entrega.id}/messages`);
      setMsgs(r.data);
    } catch (e) { console.warn("chat load", e); }
  };
  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [open, entrega?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/entregas/${entrega.id}/messages`, { text });
      setMsgs((prev) => [...prev, r.data]);
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="chat-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-600"/>
            {title || `Chat · ${entrega?.numero_pedido || "Entrega"}`}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col h-[400px]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 p-3 bg-slate-50 rounded-lg border">
            {msgs.length === 0 && <p className="text-center text-muted-foreground text-sm py-8">Nenhuma mensagem ainda. Comece a conversa!</p>}
            {msgs.map((m) => {
              const mine = m.sender_id === currentUserId;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-orange-600 text-white" : "bg-white border border-slate-200"}`}>
                    <div className={`text-[10px] font-semibold ${mine ? "text-orange-100" : "text-orange-700"}`}>
                      {mine ? "Você" : m.sender_name} · {m.sender_role}
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[10px] mt-1 ${mine ? "text-orange-100" : "text-muted-foreground"}`}>{fmtHora(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={send} className="flex gap-2 mt-3">
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Digite sua mensagem..." data-testid="chat-input" autoFocus/>
            <Button type="submit" disabled={sending || !text.trim()} className="bg-orange-600 hover:bg-orange-700" data-testid="chat-send">
              <Send className="w-4 h-4"/>
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
